export const BROADCAST_READ_CACHE_TTL_MS = 4 * 24 * 60 * 60 * 1000
export const BROADCAST_READ_CACHE_MAX_IDS = 100

export interface StoredBroadcastId {
  id: string
  recordedAt: number
}

export interface BroadcastStateMessage {
  id: string
  seq: number
  time: string
}

export interface BroadcastTickerState {
  id: string
  level: 'normal' | 'warning'
  text: string
}

export interface BroadcastPanelStatus {
  unreadCount: number
  ticker: BroadcastTickerState | null
}

export interface BroadcastUiState<T extends BroadcastStateMessage> {
  messages: T[]
  activeMessage: T | null
  emergencyMessage: T | null
  emergencyQueue: T[]
  previewMessageId: string
}

export function broadcastReadStorageKey(eventId: string, channel = 'audience') {
  const normalizedEventId = String(eventId || '').trim()
  return normalizedEventId ? `venue-h5-broadcast-read-v1:${normalizedEventId}:${channel}` : ''
}

export function readStoredBroadcastIds(storage: Storage, key: string, now = Date.now()): StoredBroadcastId[] {
  if (!key) return []
  try {
    const stored = JSON.parse(storage.getItem(key) || 'null') as { version?: number; entries?: Array<{ id?: unknown; recordedAt?: unknown }> } | unknown[] | null
    const legacy = Array.isArray(stored)
    const rawEntries = legacy
      ? stored.map((id) => ({ id, recordedAt: now }))
      : (Array.isArray(stored?.entries) ? stored.entries : [])
    const seen = new Set<string>()
    const entries = rawEntries.map((entry) => ({
      id: String(entry?.id || ''),
      recordedAt: Number(entry?.recordedAt),
    })).filter((entry) => entry.id
      && Number.isFinite(entry.recordedAt)
      && now - entry.recordedAt < BROADCAST_READ_CACHE_TTL_MS
      && !seen.has(entry.id)
      && Boolean(seen.add(entry.id)))
      .slice(0, BROADCAST_READ_CACHE_MAX_IDS)
    if (legacy || stored?.version !== 2 || entries.length !== rawEntries.length) {
      try { storage.setItem(key, JSON.stringify({ version: 2, entries })) } catch { /* Read results remain usable when cleanup cannot be persisted. */ }
    }
    return entries
  } catch {
    return []
  }
}

export function rememberStoredBroadcastId(storage: Storage, key: string, messageId: string, now = Date.now()) {
  const id = String(messageId || '').trim()
  if (!key || !id) return
  const entries = [
    { id, recordedAt: now },
    ...readStoredBroadcastIds(storage, key, now).filter((entry) => entry.id !== id),
  ].slice(0, BROADCAST_READ_CACHE_MAX_IDS)
  storage.setItem(key, JSON.stringify({ version: 2, entries }))
}

function sortMessages<T extends BroadcastStateMessage>(rows: T[]) {
  return rows.sort((left, right) => right.seq - left.seq || Date.parse(right.time) - Date.parse(left.time))
}

/**
 * Replace the pre-request part of the list with the authoritative history snapshot,
 * while retaining SSE messages that arrived after the request started.
 */
export function reconcileBroadcastHistory<T extends BroadcastStateMessage>(
  current: T[],
  history: T[],
  requestSeq: number,
  revokedSeqById: ReadonlyMap<string, number> = new Map(),
): T[] {
  const allowed = (message: T) => message.seq > (revokedSeqById.get(message.id) ?? -1)
  const arrivedDuringRequest = current.filter((message) => message.seq > requestSeq && allowed(message))
  const byId = new Map<string, T>()
  history.filter(allowed).forEach((message) => byId.set(message.id, message))
  arrivedDuringRequest.forEach((message) => byId.set(message.id, message))
  return sortMessages([...byId.values()]).slice(0, 100)
}

export function revokeBroadcastState<T extends BroadcastStateMessage>(state: BroadcastUiState<T>, id: string): BroadcastUiState<T> {
  return {
    messages: state.messages.filter((message) => message.id !== id),
    activeMessage: state.activeMessage?.id === id ? null : state.activeMessage,
    emergencyMessage: state.emergencyMessage?.id === id ? null : state.emergencyMessage,
    emergencyQueue: state.emergencyQueue.filter((message) => message.id !== id),
    previewMessageId: state.previewMessageId === id ? '' : state.previewMessageId,
  }
}
